import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useMasterOptions } from './useMasterOptions.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { DateInput, validateDateSubmission } from '../../../../shared/components/DateInput.js';

/** M17RateType / M17RateUnit. A value outside these is rejected with 400. */
const RATE_TYPES = [
  'PER_SQ_FT', 'PER_SQ_METER', 'PER_BOARD', 'PER_POLE', 'PER_FLEX',
  'PER_HOARDING', 'PER_WALL_PAINTING', 'PER_UNIT', 'PER_ACTIVITY',
  'FIXED_COST', 'COMPOSITE_COST', 'CAMPAIGN_COST',
] as const;

const RATE_UNITS = [
  'SQ_FT', 'SQ_METER', 'UNIT', 'BOARD', 'ACTIVITY', 'LUMP_SUM',
] as const;

interface VendorOption {
  readonly vendorId: string;
  readonly vendorCode?: string;
  readonly vendorName?: string;
}

/** L17.02 — New rate card (`/admin/rate-master/cards/new`).
 *  Posts CreateRateMasterDto: t200_rate_master stores one rate row, not a card
 *  header with components, and rate_value is rupees at numeric(14,4). */
export function M17RateCardNewPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const nav = useNavigate();
  const { brandingCodes, brandingTypes, territoryCodes, uomCodes, brandingItems, loadError } = useMasterOptions(token);
  const [vendors, setVendors] = useState<readonly VendorOption[]>([]);
  // A standard card needs no vendor, so a reader who may not list vendors is
  // offered the standard card only; any other failure is said.
  const [vendorsForbidden, setVendorsForbidden] = useState(false);
  const [vendorError, setVendorError] = useState<string | null>(null);
  const [brandingTypeCode, setBrandingTypeCode] = useState('');
  const [territoryCode, setTerritoryCode] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');
  const [rateInr, setRateInr] = useState('');
  const [rateType, setRateType] = useState<string>('PER_SQ_FT');
  const [rateUnit, setRateUnit] = useState<string>('SQ_FT');
  const [uomCode, setUomCode] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [brandingItemId, setBrandingItemId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: VendorOption[] }>('/v1/master/vendors', { token })
      .then((r) => { if (alive) setVendors(r.data ?? []); })
      .catch((e) => {
        if (!alive) return;
        if (e instanceof ApiError && e.status === 403) setVendorsForbidden(true);
        else setVendorError((e as Error).message);
      });
    return () => { alive = false; };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!brandingTypeCode) { setError(t('m17.rateCardNew.errBranding')); return; }
    if (!effectiveFrom || !effectiveTo) { setError(t('m17.rateCardNew.errDates')); return; }
    if (effectiveTo <= effectiveFrom) { setError(t('m17.rateCardNew.errDateOrder')); return; }
    const rate = Number(rateInr);
    if (!Number.isFinite(rate) || rate < 0) { setError(t('m17.rateCardNew.errRate')); return; }

    // rate_code and rate_name are NOT NULL but the form asks for neither, so
    // they are derived from what the user did choose and stay reproducible.
    const suffix = effectiveFrom.replace(/-/g, '');
    const body: Record<string, unknown> = {
      rateCode: `${brandingTypeCode}-${rateType}-${suffix}`,
      rateName: `${brandingTypeCode} ${rateType} from ${effectiveFrom}`,
      rateType,
      rateValue: rate,
      rateUnit,
      priorityLevel: 5,
      effectiveFromDate: effectiveFrom,
      effectiveToDate: effectiveTo,
    };
    if (territoryCode) body.territoryCode = territoryCode;
    if (uomCode) body.description = `UOM ${uomCode}`;
    // Omitted, not null: a NULL vendor is what marks the standard card.
    if (vendorId) body.vendorId = vendorId;
    if (brandingItemId) body.brandingItemId = brandingItemId;
    // The field takes a code, but t200 stores the id — without this the rate
    // card's branding-type filter cannot see hand-entered rows.
    const matchedType = brandingTypes.find((b) => b.typeCode === brandingTypeCode);
    if (matchedType) body.brandingTypeId = matchedType.brandingTypeId;

    try {
      const r = await apiRequest<{ rateId: string }>('/v1/m17/rates', {
        method: 'POST',
        token,
        body,
      });
      nav(`/admin/rate-master/cards/${r.rateId}`);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <main aria-label="rate-card-new">
      <h1>{t('m17.rateCardNew.title')}</h1>
      {loadError ? <p role="alert">{loadError}</p> : null}
      {vendorError ? <p role="alert">{vendorError}</p> : null}
      <form onSubmit={onSubmit} aria-label="rate-card-new-form" noValidate onSubmitCapture={validateDateSubmission}>
        <label htmlFor="rc-branding">{t('m17.rateCardNew.brandingType')}</label>
        <input id="rc-branding" list="rc-branding-options" value={brandingTypeCode} onChange={(e) => setBrandingTypeCode(e.target.value)} />
        <datalist id="rc-branding-options">
          {brandingCodes.map((c) => <option key={c} value={c} />)}
        </datalist>

        <label htmlFor="rc-rate-type">{t('m17.rateCardNew.rateType')}</label>
        <select id="rc-rate-type" value={rateType} onChange={(e) => setRateType(e.target.value)}>
          {RATE_TYPES.map((c) => <option key={c} value={c}>{unitLabel(c)}</option>)}
        </select>

        <label htmlFor="rc-rate-unit">{t('m17.rateCardNew.rateUnit')}</label>
        <select id="rc-rate-unit" value={rateUnit} onChange={(e) => setRateUnit(e.target.value)}>
          {RATE_UNITS.map((c) => <option key={c} value={c}>{unitLabel(c)}</option>)}
        </select>

        <label htmlFor="rc-territory">{t('m17.rateCardNew.territory')}</label>
        <input id="rc-territory" list="rc-territory-options" value={territoryCode} onChange={(e) => setTerritoryCode(e.target.value)} />
        <datalist id="rc-territory-options">
          {territoryCodes.map((c) => <option key={c} value={c} />)}
        </datalist>

        {vendorsForbidden ? null : (
          <>
            <label htmlFor="rc-vendor">{t('m17.rateCardNew.vendor')}</label>
            <select id="rc-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
              <option value="">{t('m17.rateCardNew.standardCard')}</option>
              {vendors.map((v) => (
                <option key={v.vendorId} value={v.vendorId}>{v.vendorName ?? v.vendorCode ?? v.vendorId}</option>
              ))}
            </select>
          </>
        )}

        <label htmlFor="rc-item">{t('m17.rateCardNew.brandingItem')}</label>
        <select id="rc-item" value={brandingItemId} onChange={(e) => setBrandingItemId(e.target.value)}>
          <option value="">{t('m17.rateCardNew.allItems')}</option>
          {brandingItems.map((i) => (
            <option key={i.brandingItemId} value={i.brandingItemId}>{i.label}</option>
          ))}
        </select>

        <label htmlFor="rc-from">{t('m17.rateCardNew.effectiveFrom')}</label>
        <DateInput id="rc-from" type="date" value={effectiveFrom} onValueChange={(nextValue) => setEffectiveFrom(nextValue)} />
        <label htmlFor="rc-to">{t('m17.rateCardNew.effectiveTo')}</label>
        <DateInput id="rc-to" type="date" value={effectiveTo} onValueChange={(nextValue) => setEffectiveTo(nextValue)} />

        <label htmlFor="rc-rate">{t('m17.rateCardNew.rate')}</label>
        <input id="rc-rate" value={rateInr} onChange={(e) => setRateInr(e.target.value)} />

        <label htmlFor="rc-uom">{t('m17.rateCardNew.uom')}</label>
        <input id="rc-uom" list="rc-uom-options" value={uomCode} onChange={(e) => setUomCode(e.target.value)} />
        <datalist id="rc-uom-options">
          {uomCodes.map((c) => <option key={c} value={c} />)}
        </datalist>

        {error ? <p role="alert">{error}</p> : null}
        <button type="submit">{t('m17.rateCardNew.createBtn')}</button>
      </form>
    </main>
  );
}
